import React, {useEffect, useState} from "react";
import {Outlet, useLocation, useNavigate} from "react-router-dom"
import {useDispatch} from "react-redux";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { Header, Footer, ModalBig } from '@vedmarket/ui-kit';

import DemoNotification from "../common/demo-notification";
import {getCookie, setCookie} from "../../utils/cookies";
import {
  useUser,
  getToken,
  removeToken,
  setAccessDenied,
  useUpdateUserInfoMutation,
  setToken,
  useSignInMutation,
  useAccessDenied,
  useUserIsBroker, useUserIsManager
} from "../../api/auth";
import {API_URL, CHAT_API_URL, IS_AGREED_DEMO, ROUTES, SOCKET_IO_ENABLED, SUPPORT_CHAT_SOCKET_URL} from "../../config/constants";
import RealtimeSupportChat from "../common/realtime-support-chat";

import {useAuthContext} from "../../context/AuthContext";
import {useLazyGetDictionaryLanguagesQuery} from "../../api/localization";
import {changeAppLanguage} from "../../config/i18";

import './cabinet.scss'
import {useGetBrokerServicesAllQuery} from "../../api/broker";
import AccessDeniedPlaceholder from "./accessDeniedPlaceholder";
import CabinetMenu from '../common/cabinet-menu';
import { resetAppSessionForNewUser, resetCalculationSession } from '../../utils/calculationSession';

const Cabinet = () => {

  const user = useUser();
  const authToken = getToken() || undefined;
  const { setIsSuccess, setUser } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation()
  const dispatch = useDispatch();
  const accessDenied = useAccessDenied();

  const userIsBroker = Boolean(useUserIsBroker());
  const userIsManager = Boolean(useUserIsManager());

  const [ updateUserInfo ] = useUpdateUserInfoMutation()
  const [ signIn ] = useSignInMutation()

  const [ localizationGetLanguagesRequest, { data: languages } ] = useLazyGetDictionaryLanguagesQuery();

  useEffect(() => {
    localizationGetLanguagesRequest('');
  }, [ localizationGetLanguagesRequest ]);

  useEffect(() => {
    if (user?.lang) {
      changeAppLanguage(user.lang);
    }
  }, [ user?.lang ]);

  useEffect(() => {
    dispatch(setAccessDenied(false));
  }, [ dispatch, location.pathname ]);

  const { data: brokerServices  } = useGetBrokerServicesAllQuery(undefined, {
    skip: !userIsBroker
  })

  const [ isAgreed, setIsAgreed ] = useState(
    getCookie(`${IS_AGREED_DEMO}_${user?.id}`) === "true"
  );

  useEffect(() => {
    setIsAgreed(getCookie(`${IS_AGREED_DEMO}_${user?.id}`) === "true");
  }, [ user?.id ]);

  const handleSetIsAgreed = (isAgreed: boolean) => {
    setCookie(`${IS_AGREED_DEMO}_${user?.id}`, isAgreed.toString());
    setIsAgreed(isAgreed);
  }

  const handleNavigateHome = () => {
    dispatch(setAccessDenied(false));
    navigate('/', { replace: true });
  }

  const handleLogout = () => {
    resetCalculationSession(dispatch);
    setIsSuccess(false);
    setUser(undefined);
    removeToken();
    window.location.replace(ROUTES.LOGIN);
  };

  const changeLanguage = () => {
    changeAppLanguage(user?.lang);
  }

  const navigateForgetPassword = () => {
    removeToken();
    setUser(undefined);
    navigate(ROUTES.FORGOT_PASSWORD);
  }

  const sendRequestAuth = async (data: AuthRequest) => {

    try {
      const response: any = await signIn(data).unwrap();

      // set the token
      setToken(response?.jwt);

      resetAppSessionForNewUser(dispatch);

      // set the user
      setUser(response?.user);

      const isCompanyProfile = response?.user?.is_superuser || response?.user?.company_profile?.company?.id;
      const isLocalHost = [ "localhost" ].includes(window.location.hostname);

      if (!isLocalHost && isCompanyProfile) {
        window.location.replace(`/${ROUTES.COMPANY}/${ROUTES.CABINET}`);
      } else {
        window.location.replace(`/${ROUTES.CABINET}/${ROUTES.CALCULATIONS}`);
      }

    } catch (err: any) {
      throw err?.data?.detail;
    }

  }

  const cabinetContent = accessDenied ? <div className='accessDeniedContent'>
    <AccessDeniedPlaceholder />
  </div> : <Outlet />;

  return <>
    <DemoNotification
      handleSetIsAgreed={handleSetIsAgreed}
      isAgreed={isAgreed}
    />
    <Header setUser={setUser}
      removeToken={handleLogout}
      user={user}
      languages={languages}
      updateUser={updateUserInfo}
      navigateExit={handleLogout}
      changeLanguage={changeLanguage}
      navigateForgetPassword={navigateForgetPassword}
      sendRequestAuth={sendRequestAuth}/>
    {isAgreed && <>
      <div className='layout'>
        <CabinetMenu
          user={user}
          userIsManager={userIsManager}
          brokerServices={brokerServices}
          location={location}
          navigate={navigate}
        />
        {cabinetContent}
      </div>
      <Footer user={user}/>
      {SOCKET_IO_ENABLED && Boolean(user?.id) && !userIsManager && (
        <RealtimeSupportChat
          socketUrl={SUPPORT_CHAT_SOCKET_URL}
          socketPath='/ws/socket.io'
          clientId={`lk-user-${user.id}`}
          token={authToken}
          lang={user.lang || undefined}
          historyUrl={`${CHAT_API_URL}/api/chat-api/v1/support/sessions/lk-user-${user.id}/messages/`}
          title='Чат поддержки'
        />
      )}
    </>}
    <ModalBig
      isOpen={accessDenied}
      onClose={handleNavigateHome}
      title={<span className='accessDeniedModal__title'>Доступ ограничен</span>}
      buttons={
        <div className='accessDeniedModal__actions'>
          <Button
            variant='contained'
            onClick={handleNavigateHome}
          >
            Перейти на главную
          </Button>
        </div>
      }
    >
      <Typography variant='body1'>
        У Вас недостаточно прав для доступа к этому разделу.
      </Typography>
    </ModalBig>
  </>
};

export default Cabinet
